import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, Calendar, MapPin, Phone, Mail, Users, FileText, CreditCard, Clock,
  MessageCircle, Plus, X, CheckCircle2, StickyNote, Loader2,
} from "lucide-react";
import api from "@/lib/api";
import { useLeads } from "@/context/LeadsContext";
import { Avatar, StatusBadge, fmtDateIT, relTime, operatorsLabel, contactLinks } from "./clientsUi";

const QUOTE_LABEL = { draft: "Bozza", sent: "Inviato", accepted: "Accettato", rejected: "Rifiutato" };

function OverviewRow({ icon: Icon, label, children, testid }) {
  return (
    <div className="flex items-center justify-between gap-3 py-2.5 border-b border-border last:border-b-0 text-sm" data-testid={testid}>
      <span className="inline-flex items-center gap-2 text-neutral-600"><Icon className="w-4 h-4 text-neutral-400" strokeWidth={1.5} />{label}</span>
      <span className="text-right text-neutral-900 min-w-0 truncate">{children}</span>
    </div>
  );
}

// Shared quick-detail content. Opening it performs GET /clients/{id} — the
// SAME call ClientDetail makes — so an unread lead becomes "letta" here
// deterministically and the LeadsContext badge is refreshed at once.
export default function ClientQuickPanel({ item, onClose, mobile = false, onMarkedRead }) {
  const navigate = useNavigate();
  const { refresh } = useLeads() || {};
  const [full, setFull] = useState(null);
  const [loading, setLoading] = useState(false);
  const [markedRead, setMarkedRead] = useState(false);
  const [wasUnread, setWasUnread] = useState(!!item?.is_unread_lead);

  useEffect(() => {
    if (!item?.id) return undefined;
    let alive = true;
    const unreadAtOpen = !!item.is_unread_lead;
    setWasUnread(unreadAtOpen);
    setFull(null); setMarkedRead(false); setLoading(true);
    api.get(`/clients/${item.id}`).then(({ data }) => {
      if (!alive) return;
      setFull(data);
      if (unreadAtOpen) {
        setMarkedRead(true);
        refresh?.();
        onMarkedRead?.(item.id);
      }
    }).catch(() => {}).finally(() => alive && setLoading(false));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?.id]);

  if (!item) return null;
  const notes = full?.notes ?? "";
  const links = contactLinks(item);
  const quoteStatus = full?.quote?.status || (item.has_quote ? "draft" : null);
  const quoteLabel = quoteStatus ? (QUOTE_LABEL[quoteStatus] || quoteStatus) : "Non creato";
  const openFull = () => navigate(`/app/clients/${item.id}`);

  return (
    <div className={`flex flex-col ${mobile ? "min-h-full" : "h-full"}`} data-testid="client-quick-panel">
      <div className={`flex items-center justify-between ${mobile ? "px-4 h-14 border-b border-border bg-white sticky top-0 z-10" : "px-5 pt-4"}`}>
        <button type="button" onClick={onClose} className="p-2 -ml-2 rounded-full hover:bg-neutral-100" aria-label="Chiudi" data-testid="quick-panel-close">
          {mobile ? <ArrowLeft className="w-5 h-5" strokeWidth={1.7} /> : <X className="w-4 h-4" strokeWidth={1.7} />}
        </button>
        {mobile && <span className="font-serif-heading text-lg">Cliente</span>}
        {loading ? <Loader2 className="w-4 h-4 animate-spin text-neutral-400" /> : <span className="w-4" />}
      </div>

      <div className={`${mobile ? "p-4" : "px-5 pb-5"} space-y-4 flex-1`}>
        {markedRead && (
          <div className="flex items-center gap-2 rounded-md bg-emerald-50 border border-emerald-100 text-emerald-800 text-sm px-3 py-2.5" data-testid="quick-panel-marked-read">
            <CheckCircle2 className="w-4 h-4" strokeWidth={1.8} /> Richiesta segnata come letta
          </div>
        )}

        <div className="bg-white border border-border rounded-md p-4">
          <div className="flex items-start gap-3">
            <Avatar name={item.name} unread={wasUnread} size="lg" />
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <h2 className="font-serif-heading text-2xl leading-tight truncate" data-testid="quick-panel-name">{item.name || "—"}</h2>
                <StatusBadge c={{ ...item, is_unread_lead: wasUnread }} />
              </div>
              {item.partner_name && <div className="text-sm text-neutral-500">& {item.partner_name}</div>}
              <div className="mt-2 space-y-1 text-sm text-neutral-700">
                <div className="flex items-center gap-2"><Calendar className="w-4 h-4 text-neutral-400" strokeWidth={1.5} />{fmtDateIT(item.wedding_date)}</div>
                <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-neutral-400" strokeWidth={1.5} />{item.venue || "—"}</div>
                <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-neutral-400" strokeWidth={1.5} />{item.phone || "—"}</div>
                <div className="flex items-center gap-2 min-w-0"><Mail className="w-4 h-4 text-neutral-400 shrink-0" strokeWidth={1.5} /><span className="truncate">{item.email || "—"}</span></div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white border border-border rounded-md px-4 py-2">
          <div className="font-serif-heading text-lg py-1.5">Panoramica</div>
          <OverviewRow icon={Calendar} label="Matrimonio">{fmtDateIT(item.wedding_date)}</OverviewRow>
          <OverviewRow icon={MapPin} label="Location">{item.venue || "—"}</OverviewRow>
          <OverviewRow icon={Users} label="Operatore">{operatorsLabel(item)}</OverviewRow>
          <OverviewRow icon={FileText} label="Preventivo" testid="quick-panel-quote">{quoteLabel}</OverviewRow>
          <OverviewRow icon={CreditCard} label="Pagamento">{item.payment_status_label || "—"}</OverviewRow>
          <OverviewRow icon={Clock} label="Ultima attività">{relTime(item.last_activity_at)}</OverviewRow>
        </div>

        <div className="bg-white border border-border rounded-md p-4">
          <div className="flex items-center justify-between">
            <div className="font-serif-heading text-lg inline-flex items-center gap-2"><StickyNote className="w-4 h-4 text-neutral-500" strokeWidth={1.5} /> Note</div>
            <button type="button" onClick={openFull} className="text-xs text-primary hover:underline">Modifica</button>
          </div>
          <div className="mt-2 text-sm text-neutral-700 bg-neutral-50 rounded-md p-3 min-h-[44px] whitespace-pre-wrap" data-testid="quick-panel-notes">
            {full ? (notes || <span className="text-neutral-400">Nessuna nota.</span>) : <span className="text-neutral-400">…</span>}
          </div>
        </div>
      </div>

      <div className={`${mobile ? "p-4 border-t border-border bg-white sticky bottom-0" : "px-5 pb-5"} space-y-2`}>
        <button type="button" onClick={openFull} className="w-full h-12 rounded-md bg-primary text-white font-medium inline-flex items-center justify-center gap-2 hover:bg-primary/90" data-testid="quick-panel-open-full">
          Apri scheda completa <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
        </button>
        <div className="grid grid-cols-3 gap-2 text-sm">
          <a href={links.wa || "#"} target="_blank" rel="noopener noreferrer" onClick={(e) => { if (!links.wa) e.preventDefault(); }} aria-disabled={!links.wa}
            className={`h-11 rounded-md border border-border bg-white inline-flex items-center justify-center gap-1.5 ${links.wa ? "hover:bg-neutral-50" : "text-neutral-300 cursor-not-allowed"}`} data-testid="quick-panel-whatsapp">
            <MessageCircle className="w-4 h-4 text-emerald-600" strokeWidth={1.6} /> WhatsApp
          </a>
          <a href={links.mail || "#"} onClick={(e) => { if (!links.mail) e.preventDefault(); }} aria-disabled={!links.mail}
            className={`h-11 rounded-md border border-border bg-white inline-flex items-center justify-center gap-1.5 ${links.mail ? "hover:bg-neutral-50" : "text-neutral-300 cursor-not-allowed"}`} data-testid="quick-panel-email">
            <Mail className="w-4 h-4 text-neutral-600" strokeWidth={1.6} /> Email
          </a>
          <button type="button" onClick={() => navigate(`/app/clients/${item.id}?tab=preventivo`)} className="h-11 rounded-md border border-border bg-white inline-flex items-center justify-center gap-1.5 hover:bg-neutral-50" data-testid="quick-panel-quote-btn">
            <Plus className="w-4 h-4 text-neutral-600" strokeWidth={1.6} /> <span className="truncate">{item.has_quote ? "Preventivo" : "Crea preventivo"}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
